import type { BonusPg } from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { SocietarioApi } from '../societario/societario.api.js';

const ROTULO_STATUS: Record<BonusPg['status'], { texto: string; tom: 'neutro' | 'sucesso' | 'alerta' | 'erro' | 'destaque' }> = {
  proposto: { texto: 'Proposto', tom: 'alerta' },
  aprovado: { texto: 'Aprovado', tom: 'destaque' },
  pago: { texto: 'Pago', tom: 'sucesso' },
  rejeitado: { texto: 'Rejeitado', tom: 'erro' },
  cancelado: { texto: 'Cancelado', tom: 'neutro' },
};

/**
 * `<erp-bonus-acoes-modal>` — visualização + transições de estado de um
 * Bônus PG (`proposto -> aprovado -> pago`, com `rejeitado`/`cancelado`
 * como saídas alternativas — `societario.service.ts`). As ações
 * mostradas dependem SEMPRE do status atual do bônus, nunca de uma
 * suposição: o backend é a fonte de verdade e revalida cada transição
 * (`WHERE status = <esperado>`) — se este modal mostrar uma ação
 * inválida por dado desatualizado, a chamada falha com erro explícito,
 * nunca corrompe o registro.
 *
 * "Pagar" exige um `transacaoId` já existente — o backend NUNCA cria a
 * transação sozinho (diferente do fluxo de Comissões). Este modal apenas
 * referencia; a transação real deve ser lançada antes, em Financeiro.
 */
export class ErpBonusAcoesModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _societarioApi: SocietarioApi | null = null;
  private _aprovadorId: string | null = null;
  private _aoAtualizar: ((bonus: BonusPg) => void) | null = null;

  private aberto = false;
  private bonusAtual: BonusPg | null = null;
  private nomePessoa = '';
  private modoPagar = false;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
  }

  /** id do usuário logado — usado como `aprovadorId` ao aprovar (o backend exige um ator explícito, nunca infere). */
  set aprovadorId(valor: string | null) {
    this._aprovadorId = valor;
  }

  set aoAtualizar(valor: (bonus: BonusPg) => void) {
    this._aoAtualizar = valor;
  }

  abrir(bonus: BonusPg, nomePessoa: string): void {
    this.bonusAtual = bonus;
    this.nomePessoa = nomePessoa;
    this.modoPagar = false;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async transicionar(acao: () => Promise<BonusPg>): Promise<void> {
    if (this.enviando) return;
    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const bonus = await acao();
      this.aberto = false;
      this._aoAtualizar?.(bonus);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível concluir a ação. tente novamente.';
      this.enviando = false;
      this.render();
    }
  }

  private aoAprovar(): void {
    const api = this._societarioApi;
    const bonus = this.bonusAtual;
    if (!api || !bonus) return;
    if (!this._aprovadorId) {
      this.mensagemErro = 'sessão sem usuário identificado — não é possível aprovar';
      this.render();
      return;
    }
    void this.transicionar(() => api.aprovarBonus(bonus.id, this._aprovadorId as string));
  }

  private aoRejeitar(): void {
    const api = this._societarioApi;
    const bonus = this.bonusAtual;
    if (!api || !bonus) return;
    void this.transicionar(() => api.rejeitarBonus(bonus.id));
  }

  private aoCancelar(): void {
    const api = this._societarioApi;
    const bonus = this.bonusAtual;
    if (!api || !bonus) return;
    void this.transicionar(() => api.cancelarBonus(bonus.id));
  }

  private aoConfirmarPagamento(evento: Event): void {
    evento.preventDefault();
    const api = this._societarioApi;
    const bonus = this.bonusAtual;
    if (!api || !bonus) return;

    const transacaoId = this.root.querySelector<HTMLInputElement>('#transacao-id')?.value.trim() ?? '';
    if (!transacaoId) {
      this.mensagemErro = 'informe o id da transação já lançada em Financeiro';
      this.render();
      return;
    }
    void this.transicionar(() => api.pagarBonus(bonus.id, transacaoId));
  }

  private render(): void {
    const bonus = this.bonusAtual;
    const status = bonus ? ROTULO_STATUS[bonus.status] : null;

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        .conteudo { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        dl { display: grid; grid-template-columns: auto 1fr; gap: var(--pg-space-2, 8px) var(--pg-space-4, 16px); margin: 0; font-size: var(--pg-font-size-sm, 0.875rem); }
        dt { color: var(--pg-color-texto-secundario, #9aa1ab); }
        dd { margin: 0; color: var(--pg-color-texto-primario, #f2f3f5); }
        .valor { font-size: var(--pg-font-size-lg, 1.125rem); font-weight: var(--pg-font-weight-semibold, 600); }
        .status-linha { display: flex; align-items: center; gap: var(--pg-space-3, 12px); }
        .terminal { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
        .form-pagar { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); padding-top: var(--pg-space-3, 12px); border-top: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Bônus PG</span>
        <div class="conteudo">
          ${bonus && status ? this.renderCorpo(bonus, status) : ''}
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </div>
        <div slot="footer" class="rodape">
          ${this.renderRodape(bonus)}
        </div>
      </pg-modal>
    `;

    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
    this.root.querySelector('#botao-fechar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#botao-aprovar')?.addEventListener('pg-click', () => this.aoAprovar());
    this.root.querySelector('#botao-rejeitar')?.addEventListener('pg-click', () => this.aoRejeitar());
    this.root.querySelector('#botao-cancelar-bonus')?.addEventListener('pg-click', () => this.aoCancelar());
    this.root.querySelector('#botao-abrir-pagar')?.addEventListener('pg-click', () => {
      this.modoPagar = true;
      this.render();
    });

    const formPagar = this.root.querySelector('#form-pagar');
    formPagar?.addEventListener('submit', (evento) => this.aoConfirmarPagamento(evento));
    this.root.querySelector('#botao-confirmar-pagar')?.addEventListener('pg-click', (evento) => this.aoConfirmarPagamento(evento));
  }

  private renderCorpo(bonus: BonusPg, status: { texto: string; tom: string }): string {
    return `
      <div class="status-linha">
        <pg-badge tone="${status.tom}">${escaparHtml(status.texto)}</pg-badge>
        <span class="valor">${formatBRL(centavosParaReais(bonus.valorCentavos))}</span>
      </div>
      <dl>
        <dt>Pessoa</dt><dd>${escaparHtml(this.nomePessoa)}</dd>
        <dt>Competência</dt><dd>${escaparHtml(bonus.competencia)}</dd>
        <dt>Motivo</dt><dd>${escaparHtml(bonus.motivo)}</dd>
        ${bonus.observacao ? `<dt>Observação</dt><dd>${escaparHtml(bonus.observacao)}</dd>` : ''}
        ${bonus.transacaoId ? `<dt>Transação</dt><dd>${escaparHtml(bonus.transacaoId)}</dd>` : ''}
      </dl>
      ${this.renderAcoesPorStatus(bonus)}
    `;
  }

  private renderAcoesPorStatus(bonus: BonusPg): string {
    if (bonus.status === 'pago' || bonus.status === 'rejeitado' || bonus.status === 'cancelado') {
      return `<p class="terminal">Estado final — nenhuma ação disponível.</p>`;
    }
    if (bonus.status === 'aprovado' && this.modoPagar) {
      return `
        <form id="form-pagar" class="form-pagar" novalidate>
          <label>
            Id da transação (já lançada em Financeiro)
            <input id="transacao-id" type="text" placeholder="uuid da transação" />
          </label>
        </form>
      `;
    }
    return '';
  }

  private renderRodape(bonus: BonusPg | null): string {
    if (!bonus) return `<pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>`;

    if (bonus.status === 'proposto') {
      return `
        <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
        <pg-button id="botao-rejeitar" variant="secondary" ${this.enviando ? 'loading' : ''}>Rejeitar</pg-button>
        <pg-button id="botao-aprovar" variant="primary" ${this.enviando ? 'loading' : ''}>Aprovar</pg-button>
      `;
    }

    if (bonus.status === 'aprovado') {
      if (this.modoPagar) {
        return `
          <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
          <pg-button id="botao-confirmar-pagar" variant="primary" ${this.enviando ? 'loading' : ''}>Confirmar pagamento</pg-button>
        `;
      }
      return `
        <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
        <pg-button id="botao-cancelar-bonus" variant="secondary" ${this.enviando ? 'loading' : ''}>Cancelar</pg-button>
        <pg-button id="botao-abrir-pagar" variant="primary" ${this.enviando ? 'loading' : ''}>Pagar</pg-button>
      `;
    }

    return `<pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>`;
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-bonus-acoes-modal', ErpBonusAcoesModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-bonus-acoes-modal': ErpBonusAcoesModal;
  }
}
